/**
 * `GET /api/config` — the RESOLVED operational config that the client reflects.
 *
 * CONFIG_OVER_CODE §3 closes the circle here: the `adcaps.*` (and language
 * policy, and module toggles) were already resolved on the server —defaults →
 * env → overrides → clamps—, audited and edited in `/admin/config`…
 * but if the client keeps using its local constants, **changing the value changes
 * nothing**. A promise of config-over-code that does not reach behavior
 * is only half a promise.
 *
 * Decisions:
 *   - **Public and guest-first**: the endpoint requires no session (ad capping
 *     exists without an account), so neither does the hook.
 *   - **REST, never PowerSync**: it is configuration, not user data.
 *   - **Fails towards code default**: without a response (offline, server
 *     down, cold start) returns `undefined` and each consumer uses its
 *     local constant — exactly the historical behavior. Zero
 *     retries: an ad cap does not justify hammering the rate-limiter.
 *   - **A single query for the whole app**: TanStack deduplicates by
 *     `appConfigQueryKeys.public()`, so calling it from multiple places does not
 *     multiply requests nor require mounting a provider in a specific order.
 */
import type { Accessor } from "solid-js";
import { appConfigQueryKeys, type components } from "@listto/shared";
import { api, createApiQuery } from "@/lib/api";

type ErrorModel = components["schemas"]["ErrorModel"];
export type PublicConfig = components["schemas"]["PublicConfigResponse"];

/**
 * Public config refresh. A change in the back-office is seen **without
 * redeploy**; this is the maximum interval to see it without reloading the tab.
 */
const PUBLIC_CONFIG_STALE_MS = 300_000;

/**
 * Resolved public config, or `undefined` while there is no response.
 *
 * Returning `undefined` (and not an object with populated defaults) is deliberate:
 * it forces each consumer to express its own code default, which is what
 * must continue to govern when the server does not answer.
 */
export function usePublicConfig(): Accessor<PublicConfig | undefined> {
  const query = createApiQuery<PublicConfig, ErrorModel>(
    () => appConfigQueryKeys.public(),
    async () => api.GET("/api/config", {}),
    () => ({ staleTime: PUBLIC_CONFIG_STALE_MS, retry: false }),
  );
  return () => query.data;
}
